import { ArrowLeft, ChevronDown, Loader2, Search } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { User } from "@/schema";
import {
  activeContextStore,
  type ActiveUserSummary,
} from "../services/ActiveContextStore";
import { Tooltip } from "./Tooltip";

interface AdminUserSwitcherProps {
  user: User | null;
  activeUser: User;
  compact?: boolean;
}

interface RequestError {
  message?: string;
  response?: { data?: { message?: string; error?: string } };
}

const requestErrorMessage = (error: unknown, fallback: string): string => {
  const requestError = error && typeof error === "object"
    ? error as RequestError
    : null;
  return requestError?.response?.data?.message
    || requestError?.response?.data?.error
    || requestError?.message
    || fallback;
};

const userLabel = (item: ActiveUserSummary | undefined, fallback?: string | null): string => {
  const fullName = String(item?.fullName || "").trim();
  if (fullName) return fullName;
  const firstLast = [item?.firstName, item?.lastName].filter(Boolean).join(" ").trim();
  if (firstLast) return firstLast;
  const email = String(item?.email || item?.primaryEmail || item?.primaryEmailAddress || "").trim();
  if (email) return email;
  return String(item?.name || item?.id || fallback || "");
};

export const AdminUserSwitcher = ({
  user,
  activeUser,
  compact = false,
}: AdminUserSwitcherProps) => {
  const adminId = user?.id || "";
  const [availableUsers, setAvailableUsers] = useState<ActiveUserSummary[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [switchingUserId, setSwitchingUserId] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  const loadUsers = useCallback(async (force = false) => {
    if (!adminId || user?.type !== "admin") return;
    setIsLoading(true);
    setError(null);
    try {
      const items = await activeContextStore.users(adminId, force);
      setAvailableUsers(items);
    } catch (error: unknown) {
      setError(requestErrorMessage(error, "Failed to load users."));
    } finally {
      setIsLoading(false);
    }
  }, [adminId, user?.type]);

  useEffect(() => {
    if (user?.type !== "admin") return;
    setAvailableUsers([]);
    loadUsers();
  }, [loadUsers, user?.type]);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  const filteredUsers = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return availableUsers;
    return availableUsers.filter((item) =>
      userLabel(item).toLowerCase().includes(query)
      || String(item?.id || "").toLowerCase().includes(query));
  }, [availableUsers, searchQuery]);

  const returnToAdmin = () => {
    const adminOrgId = String(user?.activeOrgId || "").trim();
    if (!adminOrgId) {
      setError("Your organization is unavailable.");
      return;
    }
    activeContextStore.selectUser({
      id: String(user.id),
      type: user.type,
      activeOrgId: user.activeOrgId,
    });
    window.location.reload();
  };

  const handleActAsUser = (targetUser: ActiveUserSummary) => {
    if (targetUser.id === user?.id) {
      returnToAdmin();
      return;
    }
    setSwitchingUserId(targetUser.id);
    setError(null);
    try {
      // One atomic persisted scope move. Reload authenticates and hydrates this
      // exact user/org pair; React state never owns the transition.
      activeContextStore.selectUser(targetUser);
      window.location.reload();
    } catch (error: unknown) {
      setError(requestErrorMessage(error, "Failed to switch user."));
      setSwitchingUserId(null);
    }
  };

  if (!user || user.type !== "admin") return null;
  const isDelegated = activeUser.id !== user.id;
  const activeListUser = availableUsers.find((item) => item.id === activeUser?.id);

  return (
    <div ref={dropdownRef} className="relative flex items-center gap-1.5" data-switcher="admin-user">
      {isDelegated && (
        <Tooltip label="Back to admin user" description="Stop acting as this user and reload your own session.">
          <button
            type="button"
            onClick={returnToAdmin}
            disabled={!!switchingUserId}
            aria-label="Back to admin user"
            className="shrink-0 flex items-center justify-center w-7 h-7 rounded-md border transition hover:opacity-85 disabled:opacity-60 disabled:cursor-not-allowed"
            style={{ borderColor: "var(--border-secondary)", background: "transparent", color: "var(--text-primary)" }}
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
        </Tooltip>
      )}

      <div className={`relative ${compact ? "w-[150px]" : "w-[205px]"}`}>
        <button
          type="button"
          data-active-user-id={activeUser?.id || undefined}
          onClick={() => {
            if (!isLoading) loadUsers();
            setOpen((value) => !value);
          }}
          disabled={isLoading && availableUsers.length === 0}
          aria-haspopup="menu"
          aria-expanded={open}
          className="w-full flex items-center justify-between gap-2 px-3 py-1.5 rounded-md border transition disabled:opacity-60 disabled:cursor-not-allowed hover:opacity-85"
          style={{ borderColor: "var(--border-secondary)", background: "transparent", color: "var(--text-primary)" }}
        >
          <div className="min-w-0 text-left leading-[1.1]">
            <div className="text-[13px] font-medium truncate">
              {userLabel(activeListUser, activeUser?.id)}
            </div>
            {!compact && (
              <div className="text-[11px] truncate mt-0.5" style={{ color: "var(--text-muted)" }}>
                {isDelegated ? "acting as user" : "platform admin"}
              </div>
            )}
          </div>
          <ChevronDown
            className={`w-3.5 h-3.5 shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
            style={{ color: "var(--text-muted)" }}
          />
        </button>

        {open && (
          <div
            className="absolute right-0 top-full mt-2 z-50 w-[320px] max-w-[calc(100vw-2rem)] rounded-lg border bg-[var(--bg-secondary)] shadow-2xl overflow-hidden"
            style={{ borderColor: "var(--border-primary)" }}
            role="menu"
          >
            <div className="p-3 space-y-2" style={{ borderBottom: "1px solid var(--border-primary)" }}>
              <div className="flex items-center gap-2 rounded-lg px-3 py-2" style={{ background: "var(--bg-tertiary)", border: "1px solid var(--border-primary)" }}>
                <Search className="w-4 h-4" style={{ color: "var(--text-muted)" }} />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  placeholder="Search users"
                  className="bg-transparent border-none outline-none text-sm flex-1"
                  style={{ color: "var(--text-primary)" }}
                />
              </div>
              {error && <div className="text-xs text-red-400">{error}</div>}
            </div>

            <div className="max-h-64 overflow-y-auto">
              {filteredUsers.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  role="menuitem"
                  className="w-full text-left px-3 py-2 transition flex flex-col gap-0.5 hover:bg-[var(--bg-tertiary)]"
                  onClick={() => handleActAsUser(item)}
                  disabled={!!switchingUserId || item.id === activeUser?.id}
                >
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-sm font-semibold truncate" style={{ color: "var(--text-primary)" }}>
                      {userLabel(item)}
                    </span>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="text-[10px] uppercase tracking-wide" style={{ color: "var(--text-muted)" }}>
                        {String(item.type || "member")}
                      </span>
                      {switchingUserId === item.id ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                    </div>
                  </div>
                  <span className="text-[11px] truncate" style={{ color: "var(--text-muted)" }}>{item.id}</span>
                </button>
              ))}
              {!isLoading && filteredUsers.length === 0 && (
                <div className="text-sm text-center py-6" style={{ color: "var(--text-muted)" }}>
                  {searchQuery ? "No users match" : "No users found"}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
